const PICTURE_SELECTOR = ".b-images-rwd__picture";
const IMAGE_SELECTOR = ".b-images-rwd__image";
const RESIZE_DEBOUNCE_DELAY = 100;

let resizeTimeout = null;
const mqlCache = new Map();

/**
 * Returns whether the supplied media query currently matches.
 *
 * @param {string} media Media query string.
 * @return {boolean} Whether the query matches.
 */
const matchesMedia = ( media ) => {

	if ( ! mqlCache.has( media ) ) {

		try {

			mqlCache.set( media, window.matchMedia( media ) );

		} catch ( error ) {

			mqlCache.set( media, null );

		}

	}

	const mql = mqlCache.get( media );

	return mql ? mql.matches : false;

};

/**
 * Returns width and height for the currently active image source.
 *
 * @param {HTMLPictureElement} picture Picture element.
 * @param {HTMLImageElement} image Image element.
 * @return {{width: string, height: string}|null} Active dimensions.
 */
const getActiveDimensions = ( picture, image ) => {

	const sources = picture.querySelectorAll( "source" );

	for ( const source of sources ) {

		const media = source.getAttribute( "media" );
		const sourceWidth = source.getAttribute( "data-width" );
		const sourceHeight = source.getAttribute( "data-height" );

		if ( ! media || matchesMedia( media ) ) {

			// Current markup sets width/height on <source> natively; only legacy markup needs patching.
			if ( source.hasAttribute( "width" ) ) {

				return null;

			}

			return {
				width: sourceWidth || "",
				height: sourceHeight || "",
			};

		}

	}

	return {
		width: image.getAttribute( "data-width" ) || image.getAttribute( "width" ) || "",
		height: image.getAttribute( "data-height" ) || image.getAttribute( "height" ) || "",
	};

};

/**
 * Updates the rendered image dimensions to match the active source.
 *
 * @param {HTMLPictureElement} picture Picture element.
 * @return {void}
 */
const updateImageDimensions = ( picture ) => {

	const image = picture.querySelector( IMAGE_SELECTOR );

	if ( ! image ) {

		return;

	}

	const dimensions = getActiveDimensions( picture, image );

	if ( ! dimensions ) {

		return;

	}

	[ "width", "height" ].forEach( ( attribute ) => {

		if ( dimensions[ attribute ] ) {

			image.setAttribute( attribute, dimensions[ attribute ] );

		} else {

			image.removeAttribute( attribute );

		}

	} );

};

/**
 * Updates all responsive images in the block.
 *
 * @return {void}
 */
const updateAllImages = () => {

	const pictures = document.querySelectorAll( PICTURE_SELECTOR );

	pictures.forEach( ( picture ) => {

		updateImageDimensions( picture );

	} );

};

/**
 * Debounces resize updates.
 *
 * @return {void}
 */
const scheduleUpdate = () => {

	if ( resizeTimeout !== null ) {

		window.clearTimeout( resizeTimeout );

	}

	resizeTimeout = window.setTimeout( () => {

		resizeTimeout = null;
		updateAllImages();

	}, RESIZE_DEBOUNCE_DELAY );

};

if ( document.readyState === "loading" ) {

	document.addEventListener( "DOMContentLoaded", updateAllImages );

} else {

	updateAllImages();

}

window.addEventListener( "resize", scheduleUpdate );
